import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import * as mfaService from "@/services/mfa";

interface MfaChallengeProps {
  onVerified: () => void;
  onCancel: () => void;
}

const MfaChallenge = ({ onVerified, onCancel }: MfaChallengeProps) => {
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setVerifying(true);

    // Get the user's verified TOTP factor
    const { data: factors } = await mfaService.listFactors();
    const totpFactor = factors?.totp?.find((f: any) => f.status === "verified");

    if (!totpFactor) {
      toast({ title: "Error", description: "No 2FA factor found.", variant: "destructive" });
      setVerifying(false);
      return;
    }

    const { error } = await mfaService.verifyTOTP(totpFactor.id, code);
    if (error) {
      toast({ title: "Invalid code", description: "The verification code was incorrect. Please try again.", variant: "destructive" });
      setCode("");
      setVerifying(false);
      return;
    }

    onVerified();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm shadow-elevated animate-scale-in">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary">
            <Shield className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="font-display">Two-Factor Verification</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app to continue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Verification Code</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="font-mono text-center text-2xl tracking-[0.5em]"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleVerify()}
            />
          </div>
          <Button
            onClick={handleVerify}
            className="w-full gradient-primary text-primary-foreground font-semibold"
            disabled={code.length !== 6 || verifying}
          >
            {verifying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Verify
          </Button>
          <Button variant="ghost" className="w-full text-muted-foreground" onClick={onCancel}>
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MfaChallenge;
